import React from 'react';

// Shared line icons (24x24, currentColor). Each accepts standard SVG props —
// callers pass `className` (size/color) and may override `strokeWidth`, so
// migrating an inline icon to these is visually identical. Defaults match the
// most common usage; pass overrides where a call site differs.
type IconProps = React.SVGProps<SVGSVGElement>;

const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const SearchIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export const CheckIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const XIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const CheckCircleIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

export const ClockIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export const FolderIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

export const BeakerIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" />
    <path d="M6.453 15h11.094" />
    <path d="M9 2h6" />
  </svg>
);

export const ChevronLeftIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export const ChevronRightIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const FilterIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// Search variant using a short diagonal path for the handle (vs. the line-based SearchIcon).
export const SearchAltIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const PlusIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const EyeIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EditIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

export const TrashIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

// Trash with vertical bars over the lid (used in tag/user lists).
export const TrashLinesIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// Trash variant with simplified body/handles (operational chat).
export const TrashAltIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

// Paper-plane (line + filled triangle outline).
export const SendIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// Lucide-style paper-plane (two paths).
export const SendPlaneIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </svg>
);

// Document/receipt (file with folded corner + lines).
export const ReceiptIcon: React.FC<IconProps> = ({ className = 'w-8 h-8', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// Scalloped receipt (jagged bottom edge).
export const ReceiptScallopedIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M4 2v20l3-2 2 2 3-2 2 2 3-2 2 2V2l-2 2-3-2-2 2-3-2-2 2-3-2z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="14" y2="13" />
  </svg>
);

// File with folded corner + lines (file-text), distinct from ReceiptIcon only by usage.
export const FileTextIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// Back chevron as a single path (vs. polyline ChevronLeftIcon).
export const ArrowBackIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

// Arrow-left with shaft + head (vs. simple chevron).
export const ArrowLeftIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

// Down chevron as a single path (vs. polyline ChevronDownIcon).
export const ChevronDownPathIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const FlagIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

export const CalendarIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

export const UserIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const UsersIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const UserPlusIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

export const UserCheckIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
);

export const UserXIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="18" y1="8" x2="23" y2="13" />
    <line x1="23" y1="8" x2="18" y2="13" />
  </svg>
);

export const MailIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

export const PhoneIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export const DollarSignIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// Office building (ClientManagement) — pillared facade with window dots.
export const BuildingIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
    <path d="M9 9v.01" />
    <path d="M9 12v.01" />
    <path d="M9 15v.01" />
    <path d="M9 18v.01" />
  </svg>
);

// Building variant used in dashboard nav (lucide building-2).
export const Building2Icon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4" />
    <path d="M10 10h4" />
    <path d="M10 14h4" />
    <path d="M10 18h4" />
  </svg>
);

export const LightningIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const TrendUpIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

export const ArrowUpRightIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

// Radiant sun/sparkle (ManagerOverview) — rays around a circle.
export const SparkleIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
    <circle cx="12" cy="12" r="4" />
  </svg>
);

// Four-point sparkles with accent strokes (AiChat).
export const SparklesIcon: React.FC<IconProps> = ({ className = 'w-6 h-6', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" />
    <path d="M19 17v4" />
    <path d="M3 5h4" />
    <path d="M17 19h4" />
  </svg>
);

export const ChartIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

export const GridIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

export const ClipboardIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);

export const SettingsIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const ShieldIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export const PowerIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

export const ActivityIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export const DownloadIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const RefreshIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

// Circle/dot used as a status indicator.
export const CircleIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Info circle with dot+stem (line variant: M12 16v-4 / M12 8h.01).
export const InfoIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

// Info circle drawn with straight lines (ConfirmModal info).
export const InfoLinesIcon: React.FC<IconProps> = ({ className = 'w-6 h-6', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// Triangle warning sign.
export const WarningIcon: React.FC<IconProps> = ({ className = 'w-6 h-6', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// Circle with an X inside (danger / x-circle).
export const XCircleIcon: React.FC<IconProps> = ({ className = 'w-6 h-6', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

// Speech bubble (message).
export const MessageIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Speech bubble with three filled dots (empty state).
export const MessageDotsIcon: React.FC<IconProps> = ({ className = 'w-12 h-12', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <circle cx="9" cy="10" r="1" fill="currentColor" />
    <circle cx="12" cy="10" r="1" fill="currentColor" />
    <circle cx="15" cy="10" r="1" fill="currentColor" />
  </svg>
);

// Folder with rounded tab (lucide folder).
export const FolderRoundedIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
  </svg>
);

// Robot/bot head.
export const RobotIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <line x1="12" y1="3" x2="12" y2="7" />
    <circle cx="9" cy="16" r="1" fill="currentColor" />
    <circle cx="15" cy="16" r="1" fill="currentColor" />
  </svg>
);

// Clock-with-hands inside circle (history).
export const HistoryIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M12 8v4l3 3" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Check-square / task (checkmark over a rounded square).
export const TaskIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 1.5, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

// Timer (clock with r=9, three-segment hands).
export const TimerIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 15" />
  </svg>
);

// Navigation arrow (paper-plane / send polygon).
export const NavigateIcon: React.FC<IconProps> = ({ className = 'w-4 h-4', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <polygon points="3 11 22 2 13 21 11 13 3 11" />
  </svg>
);

// Microphone with slash (mic muted) — standard line icon.
export const MicOffIcon: React.FC<IconProps> = ({ className = 'w-5 h-5', strokeWidth = 2, ...rest }) => (
  <svg className={className} viewBox="0 0 24 24" strokeWidth={strokeWidth} {...stroke} {...rest}>
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
